import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import NossaHistoria from "./pages/NossaHistoria";
import Servicos from "./pages/Servicos";
import Blog from "./pages/Blog";
import Contato from "./pages/Contato";
import Consultorios from "./pages/Consultorios";
import AvaliacaoCaso from "./pages/AvaliacaoCaso";
import Sitemap from "./pages/Sitemap";
import AreaPaciente from "./pages/AreaPaciente";
import MeusDados from "./pages/MeusDados";
import Preferencias from "./pages/Preferencias";
import Loja from "./pages/Loja";
import EmConstrucao from "./pages/EmConstrucao";
import { AuthProvider } from "@/hooks/useAuth";
import { useSiteMode } from "@/hooks/useSiteMode";

// Create QueryClient outside component to prevent recreation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function FloatingComponents() {
  const location = useLocation();
  const isAdminPanel = location.pathname === "/logins" && 
    new URLSearchParams(location.search).get('admin') === 'true';
  
  if (isAdminPanel) {
    return null;
  }
  
  return (
    <>
      <FloatingWhatsApp />
      <AccessibilityButton />
    </>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f4fbf7] to-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-brand-primary font-medium">Carregando...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { mode, isLoading } = useSiteMode();

  if (isLoading) {
    return <LoadingScreen />;
  }

  const isConstructionMode = mode === 'construction';

  return (
    <>
      {isConstructionMode ? (
        // Construction mode - show holding page
        <Routes>
          <Route path="*" element={<EmConstrucao />} />
        </Routes>
      ) : (
        // Production mode - show full site
        <>
          <Routes>
            <Route path="/" element={<Navigate to="/inicio" replace />} />
            <Route path="/inicio" element={<Index />} />
            <Route path="/sobre" element={<NossaHistoria />} />
            <Route path="/servicos" element={<Servicos />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/contato" element={<Contato />} />
            <Route path="/consultorios" element={<Consultorios />} />
            <Route path="/avaliacoes" element={<AvaliacaoCaso />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/logins" element={<AreaPaciente />} />
            <Route path="/meus-dados" element={<MeusDados />} />
            <Route path="/preferencias" element={<Preferencias />} />
            <Route path="/loja" element={<Loja />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <FloatingComponents />
        </>
      )}
    </>
  );
}

function AppContent() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppContent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
